import { useCallback, useState } from 'react';

/**
 * Hybrid controlled/uncontrolled state. When `controlledValue` is provided
 * it wins over the internal state and the component is "controlled"; the
 * setter just calls `onChange`. Otherwise the hook owns the state and
 * `onChange` is called as a notification alongside the internal update.
 *
 * Pattern lifted from Radix UI's `useControllableState` — collapses the
 * boilerplate around components that should support both modes.
 */
export function useControllableState<T>(
  controlledValue: T | undefined,
  onChange: ((value: T) => void) | undefined,
  defaultValue: T,
): [T, (next: T) => void] {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState<T>(defaultValue);
  const value = isControlled ? controlledValue : internalValue;

  const setValue = useCallback(
    (next: T) => {
      if (!isControlled) setInternalValue(next);
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  return [value, setValue];
}
